(ns io.daskul.webapp.widgets.form.section-nav
  "The mobile counterpart to the desktop gutter title (#704, F-060 step 7).

   On desktop a section's title is `position: sticky` inside its own section,
   so it parks beside the fields as you scroll. That IS the desktop section
   index — nothing else is needed. A phone has no gutter (breakpoints.css flips
   --gutter to 0 and --section-title-position to static), so the form loses its
   index entirely. This chip rail takes over: one chip per section, tap to
   jump, and the chip for the section you are in highlights itself.

   NO MEDIA QUERY AND NO WIDTH CHECK LIVES HERE. Visibility is
   --section-nav-display, which breakpoints.css flips in the same block that
   collapses the gutter — so the rail and the gutter cannot disagree about
   which one is on. A `window.innerWidth` check here would be a second source
   of truth for the same question, and the two would drift the first time
   either number moved.

   NO `io.daskul.webapp.ui` REQUIRE, for the reason spelled out in
   record/alerts.cljs: it transitively imports the tabler icon set, which the
   :node-test build cannot resolve, and requiring it breaks the whole cljs
   suite rather than just this namespace."
  (:require
   [clojure.string :as str]
   [io.daskul.webapp.widgets.form.section-rows :as rows]
   [uix.core :as uix :refer [defui $]]))

(def ^:private id-prefix "form-section-")

(defn anchor-id
  "The DOM id for a section's wrapper. Prefixed so a section called `state` or
   `root` cannot collide with something else on the page."
  [section-id]
  (str id-prefix (name section-id)))

(defn id->section
  "Inverse of `anchor-id`; nil for any element that is not a form section."
  [dom-id]
  (when (and dom-id (str/starts-with? dom-id id-prefix))
    (subs dom-id (count id-prefix))))

(defn nav-chips
  "The chips for a section list — one per navigable section, in declaration
   order.

   Splits with `split-sections`, the same function `sectioned-form-body` uses
   to decide what the gutter renders, so the rail and the gutter cannot list
   different sections. :sidebar sections are not form sections and get no chip
   — on a Lead that drops Follow-up, Active Trial, Loss and Activity.

   A section with no title (`:widget :activity`) is not navigable either:
   there is nothing to put on the chip."
  [sections]
  (into []
        (keep (fn [{:keys [id title]}]
                (when (and id (not (str/blank? title)))
                  {:id (name id) :title title})))
        (:main (rows/split-sections sections))))

(defn show-rail?
  "Two or more navigable sections, else nothing to navigate.

   Deliberately NOT the gutter's rule. The gutter also renders for one section
   plus loose fields, because there it has to say which fields the section
   covers — but a rail with one chip navigates nowhere and is pure furniture."
  [sections]
  (> (count (nav-chips sections)) 1))

(defn current-section
  "The section to highlight: the LAST one whose top has passed the trip line,
   or the first section while none has.

   `tops` is every section as {:id :top}, in declaration order, with :top the
   viewport-relative offset of its box.

   The obvious rule — 'the topmost section currently intersecting a band below
   the header' — is wrong at the end of the page, and the e2e caught it. The
   last section is usually the shortest (Marketing is one field). Scroll to the
   bottom and its whole box can sit ABOVE the band, so nothing intersects, and
   its chip can never light up no matter how long you look at it. Asking which
   section you have most recently scrolled past has no such blind spot, and it
   never returns nothing, so the rail cannot go stale either."
  [tops trip-line at-bottom?]
  (if (and at-bottom? (seq tops))
    ;; At the end of the page the rule above has a blind spot that no trip line
    ;; can fix: whatever follows the last section is usually shorter than the
    ;; viewport, so the page runs out of scroll while that section is still
    ;; well below the line. Measured on a Lead at 375x667: at max scroll
    ;; Marketing sits at 350px against a 120px line, so its chip could never
    ;; light up however far you scrolled. Bottom of the page means the last
    ;; section, by definition — you cannot be looking at anything else.
    (:id (last tops))
    (let [passed (filter #(<= (:top %) trip-line) tops)]
      (:id (or (last passed) (first tops))))))

(defn centre-scroll-left
  "Where the rail must scroll to put a chip in its middle, clamped at 0 so an
   early chip does not ask for a negative scroll."
  [{:keys [offset-left width rail-width]}]
  (max 0 (- (+ offset-left (/ width 2)) (/ rail-width 2))))

;; Where a section counts as "reached": just under the fixed app header plus
;; the rail itself, so a heading becomes current as it meets the rail rather
;; than while it is still hidden behind the header.
;;
;; A literal, not a token, in both places below: rootMargin is a string the
;; browser parses itself and cannot take var(), and the observer's box has to
;; agree with the number the comparison uses. 120px ≈ the 60px header plus the
;; 53px rail.
(def ^:private trip-line 120)

;; The observer is only a "recompute now" trigger — the rule above reads every
;; section's position each time. Insetting the top by the trip line makes it
;; fire exactly when a section crosses it, which is exactly when the answer can
;; change.
(def ^:private observer-margin "-120px 0px 0px 0px")

(defn- at-bottom?
  "Within a couple of pixels of the end of the page. The tolerance is for
   fractional device-pixel heights, which never sum to exactly zero."
  []
  (<= (- js/document.documentElement.scrollHeight
         js/window.innerHeight
         js/window.scrollY)
      2))

(defui section-nav
  "Sticky chip rail. `sections` is the same vector the gutter renders from."
  [{:keys [sections]}]
  (let [chips (nav-chips sections)
        [active set-active!] (uix/use-state (:id (first chips)))
        rail-ref (uix/use-ref nil)
        show? (> (count chips) 1)
        ;; `chips` is rebuilt every render, so React's Object.is on the vector
        ;; is never true and the observer would be torn down and re-created on
        ;; every keystroke in the form. A string of the ids is stable across
        ;; renders and changes exactly when the section list really changes.
        chip-key (str/join "," (map :id chips))]

    ;; Scroll-spy.
    (uix/use-effect
     (fn []
       (let [nodes (when show?
                     (seq (keep #(js/document.getElementById (anchor-id (:id %)))
                                chips)))]
         (if nodes
           (let [recompute!
                 (fn []
                   ;; Read every section, not just the ones the observer
                   ;; happened to report — the rule is "which have I scrolled
                   ;; past", which needs all of them.
                   (set-active!
                    (current-section
                     (map (fn [n] {:id (id->section (.-id n))
                                   :top (.. n getBoundingClientRect -top)})
                          nodes)
                     trip-line
                     (at-bottom?))))
                 io (js/IntersectionObserver.
                     recompute!
                     #js {:rootMargin observer-margin :threshold 0})]
             (doseq [n nodes] (.observe io n))
             ;; The observer fires on crossings, which is most of the story but
             ;; not the end of the page: approaching the bottom, no section
             ;; crosses the line, so nothing would re-evaluate `at-bottom?`.
             ;; A passive listener covers that; both call the same function.
             (js/window.addEventListener "scroll" recompute! #js {:passive true})
             (recompute!)
             (fn []
               (.disconnect io)
               (js/window.removeEventListener "scroll" recompute!)))
           ;; React rejects anything but a function or undefined.
           js/undefined)))
     ;; ^:lint/disable because `chip-key` IS the dependency on `chips` — a
     ;; stable proxy for a vector that is never Object.is-equal to itself. The
     ;; hook lint cannot see that and asks for `chips`, which would reinstate
     ;; the per-keystroke teardown the comment above describes. Suppressed
     ;; rather than left standing: a warning nobody can act on is one everybody
     ;; stops reading, and this file's other warning — a ref read that was
     ;; silently dead — sat next to it unnoticed for exactly that reason.
     ^:lint/disable [chip-key show?])

    ;; Keep the active chip in view without yanking the page.
    (uix/use-effect
     (fn []
       (when-let [rail @rail-ref]
         (when-let [chip (.querySelector rail (str "[data-chip=\"" active "\"]"))]
           (.scrollTo rail
                      #js {:left (centre-scroll-left
                                  {:offset-left (.-offsetLeft chip)
                                   :width (.-clientWidth chip)
                                   :rail-width (.-clientWidth rail)})
                           :behavior "smooth"})))
       js/undefined)
     [active])

    (when show?
      ($ :div
        {:ref rail-ref
         :data-testid "section-nav"
         :style {;; The token, not a media query. See the ns docstring.
                 :display "var(--section-nav-display, none)"
                 :position "sticky"
                 ;; NOT top: 0. The AppShell header is `position: fixed` and
                 ;; 60px tall (110 while impersonating), so a rail pinned to
                 ;; the viewport top slides underneath it. --app-shell-header-offset
                 ;; is Mantine's own live value, which is why it tracks the
                 ;; impersonation banner for free. Same expression the
                 ;; conversation aside already uses.
                 :top "var(--app-shell-header-offset, 60px)"
                 :zIndex 5
                 :gap 8
                 :alignItems "center"
                 :padding "4px var(--page-pad)"
                 :background "var(--surface-card)"
                 :borderBottom "1px solid var(--border)"
                 :overflowX "auto"
                 :scrollbarWidth "none"
                 ;; Deliberately NOT full-bleed. The card sets its padding with
                 ;; Mantine's own scale (`sm` at base), not --card-pad-x, so a
                 ;; negative margin in token units would overhang by whatever
                 ;; the two disagree by.
                 :marginBottom "var(--row-gap)"}}
        (for [{:keys [id title]} chips]
          (let [active? (= id active)]
            ($ :button
              {:key id
               :type "button"
               :data-chip id
               :data-active (when active? "true")
               :onClick (fn [_]
                          ;; scroll-margin-top on the section carries
                          ;; --anchor-offset, so the heading lands below this
                          ;; rail rather than under it. No arithmetic here.
                          (when-let [el (js/document.getElementById (anchor-id id))]
                            (.scrollIntoView el #js {:behavior "smooth" :block "start"}))
                          (set-active! id))
               :style {:flex "none"
                       ;; The hit box meets --touch-min even though the pill
                       ;; reads as ~32px — the padding carries the difference.
                       ;; A phone-only control must not sit under the touch floor.
                       :minHeight "var(--touch-min)"
                       :padding "0 13px"
                       :display "inline-flex"
                       :alignItems "center"
                       ;; The handover's SectionNav.jsx names --radius-pill; no
                       ;; such token exists here. Matching the state chip in
                       ;; pinned-facts, which is the same pill shape.
                       :borderRadius "var(--mantine-radius-xl)"
                       :whiteSpace "nowrap"
                       :cursor "pointer"
                       :fontFamily "var(--font-body)"
                       :fontSize "12.5px"
                       :fontWeight (if active? 700 600)
                       :color (if active? "var(--action-fg)" "var(--text-nav)")
                       :background (if active? "var(--action)" "var(--surface-card)")
                       :border (str "1px solid var(" (if active? "--action" "--border") ")")}}
              title)))))))
